import { fromJS } from 'immutable';
import { mergeEntity, normalizeEntities } from 'helpers/utilHelpers';

import {
  CREATE_COMMENT_SUCCESS,
  DELETE_COMMENT_SUCCESS,
  UPDATE_COMMENT_SUCCESS,
  SEND_EMOJI_SUCCESS,
  ADD_PROJECT_FAVORITE_SUCCESS,
  DELETE_PROJECT_FAVORITE_SUCCESS,
  DELETE_FILE_SUCCESS,
  LOAD_SEARCH_COMMENTS_SUCCESS,
  APPEND_ACTIVITY_LOG,
  LOAD_TASK_COMMENTS_SUCCESS,
} from 'constants/ActionTypes';
import Immutable from 'immutable';
import { COMMENT_DELETE_TYPE } from 'constants/NotificationConstants';

const initialState = fromJS({
  search: {
    comments: {},
    pagination: {},
  },
  tasks: {
    comments: {},
    pagination: {},
  },
});

const prepareComment = (comment) => {
  if (comment?.files?.length) {
    comment.files = comment.files.map((file) => file.fileId);
  }

  return comment;
};

const getCommentPath = (commentId, state) => {
  let commentTaskId = null;
  for (const [taskId, comments] of state.getIn(['tasks', 'comments'])) {
    if (comments.find((_, id) => id === commentId)) {
      commentTaskId = taskId;
    }
  }

  if (!commentTaskId) {
    return null;
  }

  return ['tasks', 'comments', commentTaskId, commentId];
};

const commentReducer = (state = initialState, action) => {
  switch (action.type) {
    case CREATE_COMMENT_SUCCESS: {
      const comment = action.values;
      if (comment.entity === 'task') {
        const taskId = comment.entityId;
        const taskCommentsPath = ['tasks', 'comments', taskId];
        const taskPaginationPath = ['tasks', 'pagination', taskId];

        if (!state.getIn(taskPaginationPath)) {
          return state;
        }

        return state.setIn(
          taskCommentsPath,
          normalizeEntities([prepareComment({ ...action.values })], 'commentId', state.getIn(taskCommentsPath)),
        );
      }

      return state;
    }

    case UPDATE_COMMENT_SUCCESS: {
      const { comment } = action;
      if (comment.entity !== 'task') {
        return state;
      }
      const path = ['tasks', 'comments', comment.entityId, comment.commentId];
      return state.setIn(path, mergeEntity(prepareComment({ ...comment }), state.getIn(path)));
    }

    case DELETE_COMMENT_SUCCESS: {
      let newState = state;
      const comment = newState.getIn(['search', 'comments', action.commentId]);
      if (comment) {
        newState = state.setIn(['search', 'comments', action.commentId, 'status'], 'deleted');
      }

      const path = getCommentPath(action.commentId, newState);
      if (!path) {
        return newState;
      }

      return newState.setIn([...path, 'status'], 'deleted');
    }

    case SEND_EMOJI_SUCCESS: {
      const path = getCommentPath(action.values.commentId, state);
      if (!path) {
        return state;
      }
      return state.setIn([...path, 'reactions'], fromJS(action.values.reactions));
    }

    case ADD_PROJECT_FAVORITE_SUCCESS: {
      if (action.favoriteMap.entity !== 'comment') {
        return state;
      }

      if (action.favoriteMap.relatedEntity.entity === 'task') {
        const taskId = action.favoriteMap.relatedEntity.entityId;
        const taskCommentsPath = ['tasks', 'comments', taskId];
        return state.setIn([...taskCommentsPath, action.favoriteMap.entityId, 'favorite'], true);
      }

      return state;
    }

    case DELETE_PROJECT_FAVORITE_SUCCESS: {
      if (action.values.entity !== 'comment') {
        return state;
      }

      if (action.meta.entity === 'task') {
        const { entityId: taskId } = action.meta;
        const taskCommentsPath = ['tasks', 'comments', taskId];
        return state.setIn([...taskCommentsPath, action.values.entityId, 'favorite'], false);
      }

      return state;
    }

    case APPEND_ACTIVITY_LOG: {
      const { type, entity, entityId, taskId } = action.payload;
      const commentsPath = ['tasks', 'comments', taskId];
      if (entity !== 'Comment') {
        return state;
      }

      if (type === COMMENT_DELETE_TYPE) {
        return state.deleteIn([...commentsPath, entityId]);
      }

      action.payload.comment.privacyUsers = action.payload.entityPrivacy;

      return state.setIn(
        commentsPath,
        normalizeEntities([prepareComment(action.payload.comment)], 'commentId', state.getIn(commentsPath)),
      );
    }

    case DELETE_FILE_SUCCESS: {
      if (!action.entity || action.entity !== 'comment' || !action.entityId) {
        return state;
      }

      const path = getCommentPath(action.entityId, state);
      if (!path) {
        return state;
      }

      const files = state.getIn([...path, 'files']);
      if (!files || !files.size) {
        return state;
      }

      return state.setIn(
        [...path, 'files'],
        files.filter((fileId) => fileId !== action.fileId),
      );
    }

    case LOAD_SEARCH_COMMENTS_SUCCESS: {
      const comments = action.comments.items.map((comment) => {
        const preparedComment = { ...comment };
        if (preparedComment?.team?.length) {
          preparedComment.team = preparedComment.team.map((memberTeam) => memberTeam.mapId);
        }

        return preparedComment;
      });

      const path = ['search', 'comments'];

      return state.setIn(path, normalizeEntities(comments, 'commentId', state.getIn(path)));
    }

    case LOAD_TASK_COMMENTS_SUCCESS: {
      const { items, _meta } = action.payload;
      const { taskId } = action.meta;
      const { currentPage, pageCount, perPage, totalCount } = _meta;

      const commentsPath = ['tasks', 'comments', taskId];
      const paginationPath = ['tasks', 'pagination', taskId];

      return state
        .setIn(
          commentsPath,
          normalizeEntities(
            items.map((item) => prepareComment({ ...item })),
            'commentId',
            state.getIn(commentsPath),
          ),
        )
        .setIn(
          paginationPath,
          fromJS({
            currentPage,
            pageCount,
            perPage,
            totalCount,
            loadedPages: state.getIn([...paginationPath, 'loadedPages'], Immutable.List()).push(currentPage),
          }),
        );
    }

    default:
      return state;
  }
};

export default commentReducer;
